import { useMemo } from 'react';

import { CrudRoleBase, crudOperations } from '../type/enum';
import { useAuth } from './useAuth';

export type CrudRolePermissions<ROLE extends string> = {
  hasRole: (role: ROLE) => boolean;
} & {
  [K in keyof typeof crudOperations]: boolean;
};

export const useCrudRole = <ROLE extends string>(
  crudRole: CrudRoleBase<ROLE> | undefined
) => {
  const { hasRole } = useAuth<ROLE>();
  return useMemo(() => {
    return Object.entries(crudOperations).reduce(
      (acc, [key, operation]) => {
        acc[key] = !crudRole || hasRole(`${crudRole}_${operation}` as ROLE);
        return acc;
      },
      {
        hasRole: (role: ROLE) => hasRole(role)
      } as CrudRolePermissions<ROLE>
    );
  }, [crudRole, hasRole]);
};

export const injectRoles =
  <
    ROLE extends string,
    PROPS extends { permissions: CrudRolePermissions<ROLE> }
  >(
    Component: React.ComponentType<PROPS>,
    crudRole: CrudRoleBase<ROLE>
  ) =>
  (props: Omit<PROPS, 'permissions'>) => {
    const role = useCrudRole<ROLE>(crudRole);

    return <Component {...({ ...props, permissions: role } as PROPS)} />;
  };
